import * as React from 'react';
import { Card, Col as BCol, Row as BRow } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { ColumnWidths, Field, FieldId, getFieldArray, getRowKey, getSectionColumnWidths, Row } from 'gossamer-unified';
import { ErrorBoundary } from '../ancillary/ErrorBoundary';
import { General } from '../fields/General';
import { SubSectionMultiProps } from '../data/QueryMulti';
import styles from './DataTile.module.css';

export interface DataTilesProps<T extends Row> extends SubSectionMultiProps<T> {
  columnWidths?: ColumnWidths;
  hideFieldLabels?: boolean;
  linkTo?: (row: T) => string;
}

interface RenderFieldProps {
  fieldId: FieldId;
  field: Field;
  hideFieldLabels?: boolean;
  value: string;
}

const RenderField: React.FC<RenderFieldProps> = (props) => {
  if (props.hideFieldLabels) {
    return <RenderFieldSub {...props} key={props.fieldId} />;
  }
  return (
    <div id={props.fieldId} key={props.fieldId} className="fieldBlock">
      {!props.hideFieldLabels && <div>{props.field.label}</div>}
      <b>
        <RenderFieldSub {...props} />
      </b>
    </div>
  );
};

const RenderFieldSub: React.FC<RenderFieldProps> = (props) => (
  <General editable={false} fieldId={props.fieldId} field={props.field} value={props.value} />
);

export const DataTiles: React.FC<DataTilesProps<any>> = (props) => {
  const children = props.rows
    .filter((row) => !props.excludeValues || props.excludeValues.indexOf(getRowKey(props.entity, row)) === -1)
    .map((row: any) => {
      const key = getRowKey(props.entity, row);
      const card = (
        <Card>
          <Card.Body>
            {getFieldArray(props.entity)
              .filter(
                ([field, fieldId]: [Field, string]) =>
                  field.visible !== false && (!!row[fieldId] || field.showEvenIfBlank),
              )
              .filter(([field]: [Field, string]) => field.listColumn)
              .map(([field, fieldId]: [Field, string]) => (
                <RenderField
                  fieldId={fieldId}
                  field={field}
                  hideFieldLabels={props.hideFieldLabels}
                  key={fieldId}
                  value={row[fieldId]}
                />
              ))}
          </Card.Body>
        </Card>
      );
      return (
        <BCol key={key} {...getSectionColumnWidths(props.columnWidths)} className="mb20px">
          <OptionalLink to={props.linkTo ? props.linkTo(row) : undefined}>{card}</OptionalLink>
        </BCol>
      );
    });
  if (props.rows.length === 0) {
    return null;
  }
  return (
    <ErrorBoundary>
      <BRow className="mb20px">{children}</BRow>
    </ErrorBoundary>
  );
};

interface OptionalLinkProps {
  to?: string;
  children: React.ReactNode;
}

const OptionalLink = (props: OptionalLinkProps) => {
  if (props.to) {
    return (
      <Link to={props.to} className={styles.DataTileAnchor}>
        {props.children}
      </Link>
    );
  }
  return <>{props.children}</>;
};
